import { useCallback, useContext, useEffect, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import _clonedeep from 'lodash.clonedeep';

import {
  SiteCustomizationStore,
  buildSiteCartItem,
  createSiteCustomizationStore,
  describeSiteCustomization,
  initSingleItemCustomization,
  missingRequiredGroup,
  restoreItemBuckets,
  selectIngredient,
  siteUnitPrice,
  snapshotItemBuckets,
} from '../helper/siteItemCustomization';

export interface ISiteItemCustomization {
  store: SiteCustomizationStore;
  /** The engine's own copy of the item (the engine annotates it). */
  item: any;
  /** The private store's `itemCustomization` state — what the item page calls `order`. */
  order: any;
  unitPrice: number;
  changes: string[];
  missingRequired: string | null;
  select: (data: any, id?: number) => void;
  buildCartItem: (quantity: number) => Record<string, any>;
}

/**
 * The site's single-item customization engine for one Berry picker session
 * (see helper/siteItemCustomization). Starts when `enabled` and an item are
 * present; on close it puts the site's shared bucket state back exactly as it
 * found it.
 */
export default function useSiteItemCustomization(
  item: any | null,
  enabled: boolean,
): ISiteItemCustomization | null {
  // Context read instead of useStore(): no throw when rendered outside the
  // site's Provider (the picker's own unit tests); the step then just waits.
  const siteStore = useContext(ReactReduxContext)?.store ?? null;
  const [session, setSession] = useState<{ store: SiteCustomizationStore; item: any } | null>(null);
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (!enabled || !item || !siteStore) return undefined;
    const snapshot = snapshotItemBuckets();
    const store = createSiteCustomizationStore(siteStore.getState() as any);
    const engineItem = _clonedeep(item);
    initSingleItemCustomization(store, engineItem);
    const unsubscribe = store.subscribe(() => setVersion((v) => v + 1));
    setSession({ store, item: engineItem });
    return () => {
      unsubscribe();
      restoreItemBuckets(snapshot, siteStore);
      setSession(null);
    };
  }, [enabled, item, siteStore]);

  const select = useCallback(
    (data: any, id?: number) => {
      if (!session) return;
      selectIngredient(session.store, data, id);
      // The site's cards read selection state straight from the buckets, so
      // re-render even when the Redux slice didn't change (item page: forceRender).
      setVersion((v) => v + 1);
    },
    [session],
  );

  if (!session) return null;
  const order = session.store.getState().itemCustomization;
  const preview = buildSiteCartItem(order, session.item, 1);
  return {
    store: session.store,
    item: session.item,
    order,
    unitPrice: siteUnitPrice(order, session.item),
    changes: describeSiteCustomization(preview),
    missingRequired: missingRequiredGroup(session.item),
    select,
    buildCartItem: (quantity: number) => buildSiteCartItem(order, session.item, quantity),
  };
}
